// @flow

// ignoring test coverage for this file until we can find a way to test logInteraction
/* istanbul ignore file */

/**
 * For use by sponsored hero.
 * Displays the sponsored profile's avatar and username, and links to the profile or an optional url.
 *
 * @author rbernard
 * @since Jan 2019
 */

import * as React from 'react';
import classNames from '@ta/common.classnames';
import { Avatar } from '@ta/styleguide.avatar';
import { Icon } from '@ta/styleguide.icon';
import { localize } from '@ta/common.i18n';
import { Link } from '@ta/common.routing';
import ObfuscatedLink from '@ta/common.obfuscated-link';
import { InteractionLogger } from '@ta/tracking.interactions';

import type { Sponsorship } from './types';

import styles from './SponsoredProfile.less';

type Props = {
  sponsorship: Sponsorship,
};

const SponsoredProfile = ({ sponsorship }: Props) => {
  const { profile, profileAvatar } = sponsorship;

  if (!profile) {
    return null;
  }

  let { url } = sponsorship;
  let isProfileLink = false;
  if (!url) {
    url = sponsorship.profile && `/Profile/${sponsorship.profile.username}`;
    isProfileLink = true;
  }
  const avatar = profileAvatar || profile.avatar;
  const children = (
    <React.Fragment>
      <div className={classNames(styles.avatar, 'is-hidden-mobile')}>
        <Avatar key={profile.id} photo={avatar} notLazy />
      </div>
      <div className={styles.text}>
        <div className={styles.sponsoredBy}>{localize('insp_sponby') /* Sponsored by */}</div>
        <div className={styles.username}>
          {profile.displayName}
          {isProfileLink ? (
            <React.Fragment>
              <Icon name="verified-fill" className={styles.verified} />
              <Icon name="checkmark" className={styles.checkmark} />
            </React.Fragment>
          ) : (
            <React.Fragment>
              &nbsp;
              <span className={styles.learnmore}>
                {localize('learn_more') /* Learn more */}
                <Icon name="external-link-no-box" className={styles.external} />
              </span>
            </React.Fragment>
          )}
        </div>
      </div>
    </React.Fragment>
  );

  return (
    <InteractionLogger>
      {logInteraction =>
        isProfileLink ? (
          <Link
            className={styles.profile}
            href={url}
            target="_self"
            onClick={e => logInteraction(e, 'heroProfileClick')}
          >
            {children}
          </Link>
        ) : (
          <ObfuscatedLink
            className={styles.profile}
            encodedUrl={url}
            target="_blank"
            onClick={e => logInteraction(e, 'heroProfileClick')}
          >
            {children}
          </ObfuscatedLink>
        )
      }
    </InteractionLogger>
  );
};

export default SponsoredProfile;
